import React, { useMemo, useState } from 'react';
import PageShell, { SectionTitle } from './PageShell';
import { Calculator, Euro, Award, Gauge, AlertTriangle, Info, ArrowRight, RefreshCw } from 'lucide-react';

// =====================================================================
// SIMULATEUR 1 — COÛT TOTAL DU PERMIS
// Source des moyennes : Sécurité Routière / observatoires des prix
// auto-école 2025-2026 (fourchettes nationales hors Île-de-France).
// =====================================================================

const FORMULES = {
  b: {
    label: 'Permis B (classique)',
    desc: '18 ans révolus. Minimum 20 h de conduite obligatoires.',
    heures_min: 20,
    examen_fee: 0,
    pack_code: 380,
    forfait_admin: 80,
    delai_moy: '6 à 9 mois',
  },
  aac: {
    label: 'AAC (Conduite Accompagnée dès 15 ans)',
    desc: 'Apprentissage anticipé. 20 h min + 3000 km avec accompagnateur sur 1 an minimum.',
    heures_min: 20,
    examen_fee: 0,
    pack_code: 380,
    forfait_admin: 110, // RDV pédagogiques inclus
    rdv_pedagogiques: 2, // 2 RDV obligatoires
    rdv_unit: 90,
    delai_moy: '12 à 24 mois',
    bonus: 'Taux de réussite +15 pts en moyenne — probatoire réduit à 2 ans',
  },
  cs: {
    label: 'Conduite Supervisée (CS, dès 18 ans)',
    desc: 'Après échec ou en complément. 20 h min · phase supervisée libre (aucun kilométrage imposé).',
    heures_min: 20,
    examen_fee: 0,
    pack_code: 380,
    forfait_admin: 100,
    rdv_pedagogiques: 1,
    rdv_unit: 90,
    delai_moy: '3 à 6 mois',
  },
  bea: {
    label: 'BEA — Boîte automatique',
    desc: 'Permis B sur véhicule à boîte automatique uniquement. 13 h min.',
    heures_min: 13,
    examen_fee: 0,
    pack_code: 380,
    forfait_admin: 80,
    delai_moy: '4 à 6 mois',
    bonus: 'Passerelle boîte méca : 7 h de formation complémentaire en école de conduite pour lever la restriction.',
  },
};

function CoutPermisSimulator() {
  const [formule, setFormule] = useState('b');
  const [heures, setHeures] = useState(FORMULES.b.heures_min);
  const [tarifHeure, setTarifHeure] = useState(48);
  const [presentations, setPresentations] = useState(1);
  const [acceptCpf, setAcceptCpf] = useState(false);

  const f = FORMULES[formule];

  // when formula changes, reset hours to minimum
  React.useEffect(() => {
    setHeures(FORMULES[formule].heures_min);
  }, [formule]);

  const detail = useMemo(() => {
    const code = f.pack_code;
    const admin = f.forfait_admin;
    const conduite = heures * tarifHeure;
    const rdv_total = (f.rdv_pedagogiques || 0) * (f.rdv_unit || 0);
    const repres = Math.max(0, presentations - 1) * 30; // 30€ par représentation pratique
    const total = code + admin + conduite + rdv_total + repres;
    const cpf = acceptCpf ? Math.min(500, total) : 0;
    return {
      code, admin, conduite, rdv_total, repres,
      total,
      cpf,
      reste: total - cpf,
    };
  }, [f, heures, tarifHeure, presentations, acceptCpf]);

  return (
    <div className="grid lg:grid-cols-[1.05fr_1fr] gap-4 sm:gap-6">
      {/* Form */}
      <div className="rounded-xl sm:rounded-2xl border border-white/10 bg-[#0d0d0e] p-5 sm:p-6 lg:p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-red-600/15 border border-red-700/30 flex items-center justify-center text-red-400"><Euro size={18} /></div>
          <div>
            <h3 className="font-serif-editorial text-white text-[22px] leading-tight">Coût total du permis</h3>
            <p className="text-white/50 text-[12.5px]">Estimation détaillée selon votre formule</p>
          </div>
        </div>

        {/* Formule */}
        <div className="mb-6">
          <label className="block text-white/70 text-[12px] uppercase tracking-wider font-semibold mb-3">Formule choisie</label>
          <div className="grid sm:grid-cols-2 gap-2">
            {Object.entries(FORMULES).map(([k, v]) => (
              <button
                key={k}
                data-testid={`cout-formule-${k}`}
                onClick={() => setFormule(k)}
                className={`text-left p-3 rounded-xl border transition ${formule === k ? 'border-red-500 bg-red-950/30' : 'border-white/10 bg-white/5 hover:border-white/20'}`}
              >
                <div className="text-white text-[13.5px] font-semibold">{v.label}</div>
                <div className="text-white/45 text-[11.5px] mt-0.5">{v.heures_min}h min · {v.delai_moy}</div>
              </button>
            ))}
          </div>
          <p className="mt-3 text-white/55 text-[12.5px] italic">{f.desc}</p>
        </div>

        {/* Heures */}
        <div className="mb-5">
          <div className="flex items-center justify-between mb-2">
            <label className="block text-white/70 text-[12px] uppercase tracking-wider font-semibold">Heures de conduite</label>
            <span className="text-red-400 font-mono-spec text-[14px] font-bold">{heures} h</span>
          </div>
          <input
            data-testid="cout-heures-slider"
            type="range" min={f.heures_min} max={40} step={1}
            value={heures} onChange={(e) => setHeures(parseInt(e.target.value))}
            className="w-full accent-red-600"
          />
          <div className="flex justify-between text-white/40 text-[11px] mt-1 font-mono-spec">
            <span>{f.heures_min}h (min légal)</span><span>Moyenne nationale ≈ 35 h</span><span>40h</span>
          </div>
        </div>

        {/* Tarif heure */}
        <div className="mb-5">
          <div className="flex items-center justify-between mb-2">
            <label className="block text-white/70 text-[12px] uppercase tracking-wider font-semibold">Tarif horaire conduite</label>
            <span className="text-red-400 font-mono-spec text-[14px] font-bold">{tarifHeure} €/h</span>
          </div>
          <input
            data-testid="cout-tarif-slider"
            type="range" min={35} max={70} step={1}
            value={tarifHeure} onChange={(e) => setTarifHeure(parseInt(e.target.value))}
            className="w-full accent-red-600"
          />
          <div className="flex justify-between text-white/40 text-[11px] mt-1 font-mono-spec">
            <span>35€ (province)</span><span>48€ (moyenne FR)</span><span>70€ (IDF)</span>
          </div>
        </div>

        {/* Présentations */}
        <div className="mb-5">
          <div className="flex items-center justify-between mb-2">
            <label className="block text-white/70 text-[12px] uppercase tracking-wider font-semibold">Présentations pratique</label>
            <span className="text-red-400 font-mono-spec text-[14px] font-bold">{presentations}</span>
          </div>
          <div className="flex gap-2">
            {[1, 2, 3].map((n) => (
              <button
                key={n}
                data-testid={`cout-presentations-${n}`}
                onClick={() => setPresentations(n)}
                className={`flex-1 h-10 rounded-xl border text-[13px] font-semibold transition ${presentations === n ? 'border-red-500 bg-red-600 text-white' : 'border-white/10 bg-white/5 text-white/70 hover:border-white/20'}`}
              >
                {n === 1 ? '1ère (incluse)' : `${n} présentations`}
              </button>
            ))}
          </div>
          <p className="text-white/40 text-[11.5px] mt-2">+30 € par représentation auto-école au-delà de la première.</p>
        </div>

        {/* CPF */}
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            data-testid="cout-cpf-checkbox"
            type="checkbox" checked={acceptCpf} onChange={(e) => setAcceptCpf(e.target.checked)}
            className="w-4 h-4 accent-red-600"
          />
          <span className="text-white/75 text-[13px]">Mobiliser mon CPF (jusqu'à 500 € pour le permis B)</span>
        </label>
      </div>

      {/* Résultat */}
      <div className="rounded-xl sm:rounded-2xl border border-red-900/40 bg-gradient-to-br from-red-950/30 via-[#150a0a] to-[#0d0d0e] p-5 sm:p-6 lg:p-8">
        <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] text-red-400 font-bold mb-3 sm:mb-4">Estimation</div>
        <div className="font-serif-editorial text-white text-[40px] sm:text-[56px] lg:text-[72px] leading-none">
          {detail.reste.toLocaleString('fr-FR')} <span className="text-[22px] sm:text-[28px] text-white/60">€</span>
        </div>
        <p className="text-white/55 text-[12px] sm:text-[13px] mt-2">Coût TTC restant à votre charge (après CPF éventuel)</p>

        <div className="mt-5 sm:mt-7 space-y-2.5 text-[12.5px] sm:text-[13.5px]">
          {[
            ['Forfait code (théorique + accès)', detail.code],
            ['Frais administratifs / inscription', detail.admin],
            [`Conduite : ${heures} h × ${tarifHeure} €`, detail.conduite],
            ...(detail.rdv_total ? [['RDV pédagogiques obligatoires', detail.rdv_total]] : []),
            ...(detail.repres ? [['Représentations pratiques', detail.repres]] : []),
          ].map(([l, v], i) => (
            <div key={i} className="flex items-center justify-between py-2 border-b border-white/5">
              <span className="text-white/70">{l}</span>
              <span className="text-white font-mono-spec">{v.toLocaleString('fr-FR')} €</span>
            </div>
          ))}
          <div className="flex items-center justify-between py-3 border-t border-white/15 mt-3">
            <span className="text-white font-semibold">Total avant CPF</span>
            <span className="text-white font-mono-spec text-[16px] font-bold">{detail.total.toLocaleString('fr-FR')} €</span>
          </div>
          {detail.cpf > 0 && (
            <div className="flex items-center justify-between py-2 text-green-400">
              <span>Prise en charge CPF</span>
              <span className="font-mono-spec">– {detail.cpf.toLocaleString('fr-FR')} €</span>
            </div>
          )}
        </div>

        {f.bonus && (
          <div className="mt-6 p-4 rounded-xl border border-green-900/40 bg-green-950/20 text-green-200/90 text-[12.5px] flex gap-2 items-start">
            <Info size={14} className="shrink-0 mt-0.5" /> {f.bonus}
          </div>
        )}

        <div className="mt-6 p-4 rounded-xl border border-yellow-900/40 bg-yellow-950/10 text-yellow-200/80 text-[11.5px] flex gap-2 items-start">
          <AlertTriangle size={13} className="shrink-0 mt-0.5" />
          <span>Estimation indicative. Les tarifs varient fortement selon la région et l'auto-école. Code Voiture ne commercialise aucune formation : comparez plusieurs établissements avant inscription.</span>
        </div>
      </div>
    </div>
  );
}

// =====================================================================
// SIMULATEUR 2 — CALCULATEUR DE POINTS
// Permis probatoire 6 pts (3 ans, 2 ans si AAC) → 12 pts définitifs
// Récupération : 1 an sans infraction = +1pt (probatoire) ;
// 2 ans sans infraction = solde plein (hors probatoire) ; stage = +4 pts (1/an)
// =====================================================================

const INFRACTIONS = [
  { id: 'exces_20_50', label: 'Excès de vitesse 20 à 50 km/h', points: 2, amende: 135 },
  { id: 'exces_30_50', label: 'Excès de vitesse 30 à 50 km/h hors agglo', points: 3, amende: 135 },
  { id: 'exces_50', label: 'Excès de vitesse > 50 km/h', points: 6, amende: 1500 },
  { id: 'feu_rouge', label: 'Franchissement feu rouge', points: 4, amende: 135 },
  { id: 'stop', label: 'Refus de priorité / non-respect stop', points: 4, amende: 135 },
  { id: 'tel', label: 'Téléphone tenu en main', points: 3, amende: 135 },
  { id: 'ceinture', label: 'Défaut ceinture / casque', points: 3, amende: 135 },
  { id: 'alcool_05', label: 'Alcoolémie 0,5 à 0,8 g/L (0,2 jeune)', points: 6, amende: 135 },
  { id: 'alcool_08', label: 'Alcoolémie ≥ 0,8 g/L (délit)', points: 6, amende: 4500 },
  { id: 'stups', label: 'Conduite après usage stupéfiants', points: 6, amende: 4500 },
  { id: 'distance', label: 'Non-respect distances de sécurité', points: 3, amende: 135 },
  { id: 'depassement', label: 'Dépassement dangereux', points: 3, amende: 135 },
  { id: 'fuite', label: 'Délit de fuite', points: 6, amende: 75000 },
];

function PointsSimulator() {
  const [probatoire, setProbatoire] = useState(false);
  const [aac, setAac] = useState(false);
  const [annees, setAnnees] = useState(1);
  const [selected, setSelected] = useState({}); // id -> count
  const [stage, setStage] = useState(false);

  const capitalInitial = probatoire ? (aac ? 6 : 6) : 12;
  const capitalMax = 12;

  const detail = useMemo(() => {
    let retrait = 0;
    let amende = 0;
    Object.entries(selected).forEach(([id, n]) => {
      const inf = INFRACTIONS.find((i) => i.id === id);
      if (inf && n > 0) {
        retrait += inf.points * n;
        amende += inf.amende * n;
      }
    });

    // Cap retrait à 8 points sur 24h cumulés... simplification : on plafonne par contrainte légale
    // (article L223-2 — maximum 8 points retirés à un même conducteur pour plusieurs infractions concomitantes)
    // mais on n'applique pas ici car on suppose des événements distincts dans le temps.

    // Récupération de points
    let bonus = 0;
    if (stage) bonus += 4;
    if (probatoire) {
      // probatoire majoration auto si pas d'infraction
      // Si infractions = 0, +2pts/an (AAC) ou +3pts/an (classique, 6 → 12 sur 3 ans)
      if (retrait === 0) {
        bonus += probatoire ? Math.min(capitalMax - capitalInitial, annees * (aac ? 3 : 2)) : 0;
      }
    } else {
      // 2 ans sans aucune infraction => solde plein
      if (retrait === 0 && annees >= 2) bonus = capitalMax - capitalInitial;
    }

    const solde = Math.max(0, Math.min(capitalMax, capitalInitial - retrait + bonus));
    const invalidation = (probatoire ? solde <= 0 : solde <= 0);
    const alerte = (probatoire && retrait >= 3) || (!probatoire && retrait >= 6);

    return { retrait, amende, bonus, solde, invalidation, alerte };
  }, [selected, probatoire, aac, annees, stage, capitalInitial, capitalMax]);

  const toggleInfraction = (id) => {
    setSelected((s) => ({ ...s, [id]: (s[id] || 0) + 1 > 3 ? 0 : (s[id] || 0) + 1 }));
  };

  const reset = () => { setSelected({}); setStage(false); setAnnees(1); };

  return (
    <div className="grid lg:grid-cols-[1.1fr_1fr] gap-4 sm:gap-6">
      <div className="rounded-xl sm:rounded-2xl border border-white/10 bg-[#0d0d0e] p-5 sm:p-6 lg:p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-red-600/15 border border-red-700/30 flex items-center justify-center text-red-400"><Award size={18} /></div>
          <div>
            <h3 className="font-serif-editorial text-white text-[22px] leading-tight">Calculateur de points</h3>
            <p className="text-white/50 text-[12.5px]">Solde du permis et récupération</p>
          </div>
        </div>

        {/* Statut */}
        <div className="grid sm:grid-cols-2 gap-2 mb-5">
          <button
            data-testid="points-confirme"
            onClick={() => { setProbatoire(false); setAac(false); }}
            className={`p-3 rounded-xl border text-left transition ${!probatoire ? 'border-red-500 bg-red-950/30' : 'border-white/10 bg-white/5 hover:border-white/20'}`}
          >
            <div className="text-white text-[13.5px] font-semibold">Permis confirmé</div>
            <div className="text-white/45 text-[11.5px] mt-0.5">12 points · majoré</div>
          </button>
          <button
            data-testid="points-probatoire"
            onClick={() => setProbatoire(true)}
            className={`p-3 rounded-xl border text-left transition ${probatoire ? 'border-red-500 bg-red-950/30' : 'border-white/10 bg-white/5 hover:border-white/20'}`}
          >
            <div className="text-white text-[13.5px] font-semibold">Permis probatoire</div>
            <div className="text-white/45 text-[11.5px] mt-0.5">6 points · 3 ans (2 ans AAC)</div>
          </button>
        </div>

        {probatoire && (
          <label className="flex items-center gap-3 mb-5 cursor-pointer">
            <input data-testid="points-aac-checkbox" type="checkbox" checked={aac} onChange={(e) => setAac(e.target.checked)} className="w-4 h-4 accent-red-600" />
            <span className="text-white/75 text-[13px]">J'ai obtenu mon permis via la conduite accompagnée (AAC)</span>
          </label>
        )}

        {/* Années écoulées sans infraction */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <label className="block text-white/70 text-[12px] uppercase tracking-wider font-semibold">Années écoulées</label>
            <span className="text-red-400 font-mono-spec text-[14px] font-bold">{annees} {annees > 1 ? 'ans' : 'an'}</span>
          </div>
          <input data-testid="points-annees-slider" type="range" min={0} max={5} step={1} value={annees} onChange={(e) => setAnnees(parseInt(e.target.value))} className="w-full accent-red-600" />
        </div>

        {/* Stage récupération */}
        <label className="flex items-center gap-3 mb-6 cursor-pointer p-3 rounded-xl border border-white/10 bg-white/[0.03]">
          <input data-testid="points-stage-checkbox" type="checkbox" checked={stage} onChange={(e) => setStage(e.target.checked)} className="w-4 h-4 accent-red-600" />
          <div>
            <span className="text-white/85 text-[13px] font-semibold">Stage de récupération suivi</span>
            <span className="text-white/45 text-[11.5px] block">+4 pts (max 1 fois par an, payant ≈ 240 €)</span>
          </div>
        </label>

        {/* Infractions */}
        <div>
          <label className="block text-white/70 text-[12px] uppercase tracking-wider font-semibold mb-3">Infractions commises (cliquez plusieurs fois)</label>
          <div className="space-y-1.5 max-h-[280px] overflow-y-auto pr-2">
            {INFRACTIONS.map((inf) => {
              const n = selected[inf.id] || 0;
              return (
                <button
                  key={inf.id}
                  data-testid={`points-infraction-${inf.id}`}
                  onClick={() => toggleInfraction(inf.id)}
                  className={`w-full text-left p-3 rounded-lg border transition flex items-center justify-between ${n > 0 ? 'border-red-500/60 bg-red-950/20' : 'border-white/10 bg-white/[0.03] hover:border-white/20'}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-white text-[13px]">{inf.label}</div>
                    <div className="text-white/45 text-[11px] font-mono-spec">−{inf.points} pts · {inf.amende.toLocaleString('fr-FR')} €</div>
                  </div>
                  {n > 0 && <span className="ml-3 px-2 py-1 rounded-md bg-red-600 text-white text-[11px] font-bold font-mono-spec">×{n}</span>}
                </button>
              );
            })}
          </div>
        </div>

        <button data-testid="points-reset" onClick={reset} className="mt-5 inline-flex items-center gap-2 text-white/55 hover:text-white text-[12.5px]">
          <RefreshCw size={12} /> Tout réinitialiser
        </button>
      </div>

      {/* Résultat */}
      <div className={`rounded-xl sm:rounded-2xl border p-5 sm:p-6 lg:p-8 ${detail.invalidation ? 'border-red-700 bg-red-950/40' : detail.alerte ? 'border-orange-700/50 bg-orange-950/20' : 'border-green-900/40 bg-gradient-to-br from-green-950/20 via-[#0a1010] to-[#0d0d0e]'}`}>
        <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] font-bold mb-3 sm:mb-4 text-white/70">Solde actuel</div>
        <div className="font-serif-editorial text-white text-[56px] sm:text-[80px] lg:text-[120px] leading-none flex items-baseline gap-3">
          {detail.solde}<span className="text-white/40 text-[24px] sm:text-[32px]">/ {capitalMax}</span>
        </div>
        <div className="mt-5 h-3 rounded-full bg-white/10 overflow-hidden">
          <div className={`h-full transition-all ${detail.solde <= 3 ? 'bg-red-500' : detail.solde <= 6 ? 'bg-orange-400' : 'bg-green-500'}`} style={{ width: `${(detail.solde / capitalMax) * 100}%` }} />
        </div>

        {detail.invalidation ? (
          <div className="mt-6 p-5 rounded-xl bg-red-950/60 border border-red-700">
            <div className="flex items-center gap-2 text-red-300 font-bold mb-1"><AlertTriangle size={16} /> INVALIDATION DU PERMIS</div>
            <p className="text-red-100/80 text-[13px]">Le permis est annulé (solde nul). Délai de repassage : 6 mois (probatoire) à 1 an. Code et conduite à repasser.</p>
          </div>
        ) : detail.alerte ? (
          <div className="mt-6 p-4 rounded-xl border border-orange-700/40 bg-orange-950/20 text-orange-200/90 text-[13px]">
            <div className="flex items-center gap-2 font-semibold mb-1"><AlertTriangle size={14} /> Lettre 48N possible</div>
            <p>Au-delà de 3 pts perdus en probatoire, l'État adresse une lettre 48N imposant un stage de sensibilisation obligatoire (sous 4 mois).</p>
          </div>
        ) : null}

        <div className="mt-7 space-y-2 text-[13.5px]">
          <div className="flex justify-between py-2 border-b border-white/5">
            <span className="text-white/70">Capital initial</span>
            <span className="text-white font-mono-spec">{capitalInitial} pts</span>
          </div>
          <div className="flex justify-between py-2 border-b border-white/5">
            <span className="text-white/70">Points retirés</span>
            <span className="text-red-400 font-mono-spec">−{detail.retrait} pts</span>
          </div>
          {detail.bonus > 0 && (
            <div className="flex justify-between py-2 border-b border-white/5">
              <span className="text-white/70">Bonus (stage + temps)</span>
              <span className="text-green-400 font-mono-spec">+{detail.bonus} pts</span>
            </div>
          )}
          <div className="flex justify-between py-2 border-b border-white/5">
            <span className="text-white/70">Amendes cumulées</span>
            <span className="text-white font-mono-spec">{detail.amende.toLocaleString('fr-FR')} €</span>
          </div>
        </div>

        <div className="mt-6 p-3 rounded-xl border border-white/10 bg-white/[0.03] text-white/55 text-[11.5px] flex gap-2 items-start">
          <Info size={12} className="shrink-0 mt-0.5" />
          <span>Source : Articles L223-1 à L223-8 du Code de la route. Le solde réel se consulte sur <strong className="text-white/80">mespointspermis.fr</strong> avec FranceConnect.</span>
        </div>
      </div>
    </div>
  );
}

// =====================================================================
// SIMULATEUR 3 — DISTANCE D'ARRÊT
// Formules officielles ECSR / Sécurité Routière :
// d_réaction = v(m/s) × t(s)  → en pratique d_réaction (m) = v(km/h) / 3.6 × 1 s
// d_freinage sec ≈ v² / 254   (μ ≈ 0,8)
// d_freinage pluie ≈ v² / 127  (μ ≈ 0,4)
// d_freinage neige ≈ v² / 90   (μ ≈ 0,28)
// d_arrêt = d_réaction + d_freinage
// =====================================================================

const ETATS = [
  { id: 'sec', label: 'Route sèche', coef: 254, color: 'bg-green-500', emoji: '☀' },
  { id: 'pluie', label: 'Route mouillée', coef: 127, color: 'bg-blue-500', emoji: '☔' },
  { id: 'neige', label: 'Route neige/verglas', coef: 90, color: 'bg-cyan-300', emoji: '❄' },
];

function DistanceArretSimulator() {
  const [vitesse, setVitesse] = useState(50);
  const [tempsReaction, setTempsReaction] = useState(1.0);
  const [etat, setEtat] = useState('sec');

  const e = ETATS.find((x) => x.id === etat);

  const detail = useMemo(() => {
    const dReact = (vitesse / 3.6) * tempsReaction;
    const dFrein = (vitesse * vitesse) / e.coef;
    const dArret = dReact + dFrein;
    // Énergie cinétique relative (v² ratio par rapport à 50 km/h)
    const ecRatio = (vitesse * vitesse) / (50 * 50);
    return {
      dReact: Math.round(dReact * 10) / 10,
      dFrein: Math.round(dFrein * 10) / 10,
      dArret: Math.round(dArret * 10) / 10,
      ecRatio: Math.round(ecRatio * 10) / 10,
    };
  }, [vitesse, tempsReaction, e]);

  // For visualization : scale on the longest measurable distance (200m)
  const maxScale = 220;
  const reactPct = Math.min(100, (detail.dReact / maxScale) * 100);
  const freinPct = Math.min(100, (detail.dFrein / maxScale) * 100);

  return (
    <div className="grid lg:grid-cols-[1fr_1.1fr] gap-4 sm:gap-6">
      <div className="rounded-xl sm:rounded-2xl border border-white/10 bg-[#0d0d0e] p-5 sm:p-6 lg:p-8">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-red-600/15 border border-red-700/30 flex items-center justify-center text-red-400"><Gauge size={18} /></div>
          <div>
            <h3 className="font-serif-editorial text-white text-[22px] leading-tight">Distance d'arrêt</h3>
            <p className="text-white/50 text-[12.5px]">Réaction + freinage selon vitesse et adhérence</p>
          </div>
        </div>

        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <label className="block text-white/70 text-[12px] uppercase tracking-wider font-semibold">Vitesse</label>
            <span className="text-red-400 font-mono-spec text-[18px] font-bold">{vitesse} km/h</span>
          </div>
          <input data-testid="dist-vitesse-slider" type="range" min={30} max={130} step={5} value={vitesse} onChange={(e) => setVitesse(parseInt(e.target.value))} className="w-full accent-red-600" />
          <div className="flex justify-between text-white/40 text-[11px] mt-1 font-mono-spec">
            <span>30 (zone)</span><span>50 (ville)</span><span>80 (route)</span><span>110 (express)</span><span>130 (autoroute)</span>
          </div>
        </div>

        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <label className="block text-white/70 text-[12px] uppercase tracking-wider font-semibold">Temps de réaction</label>
            <span className="text-red-400 font-mono-spec text-[14px] font-bold">{tempsReaction.toFixed(1)} s</span>
          </div>
          <input data-testid="dist-reaction-slider" type="range" min={0.5} max={3} step={0.1} value={tempsReaction} onChange={(e) => setTempsReaction(parseFloat(e.target.value))} className="w-full accent-red-600" />
          <div className="flex justify-between text-white/40 text-[11px] mt-1 font-mono-spec">
            <span>0,5s (pro)</span><span>1s (normal)</span><span>2s (fatigue)</span><span>3s (tél)</span>
          </div>
        </div>

        <div className="mb-2">
          <label className="block text-white/70 text-[12px] uppercase tracking-wider font-semibold mb-3">État de la chaussée</label>
          <div className="grid grid-cols-3 gap-2">
            {ETATS.map((x) => (
              <button
                key={x.id}
                data-testid={`dist-etat-${x.id}`}
                onClick={() => setEtat(x.id)}
                className={`p-3 rounded-xl border text-center transition ${etat === x.id ? 'border-red-500 bg-red-950/30' : 'border-white/10 bg-white/5 hover:border-white/20'}`}
              >
                <div className="text-[18px] mb-1">{x.emoji}</div>
                <div className="text-white text-[12px] font-semibold">{x.label}</div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Résultat + visualisation */}
      <div className="rounded-xl sm:rounded-2xl border border-red-900/40 bg-gradient-to-br from-red-950/30 via-[#150a0a] to-[#0d0d0e] p-5 sm:p-6 lg:p-8">
        <div className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] text-red-400 font-bold mb-2 sm:mb-3">Distance totale d'arrêt</div>
        <div className="font-serif-editorial text-white text-[56px] sm:text-[80px] lg:text-[110px] leading-none">
          {detail.dArret}<span className="text-[24px] sm:text-[34px] text-white/60"> m</span>
        </div>
        <p className="text-white/55 text-[12px] sm:text-[13px] mt-2">À {vitesse} km/h · {e.label.toLowerCase()} · réaction {tempsReaction.toFixed(1)}s</p>

        {/* Visualisation graphique */}
        <div className="mt-8">
          <div className="text-white/50 text-[11px] uppercase tracking-wider font-semibold mb-3">Échelle (0 → 220 m)</div>
          <div className="relative h-12 bg-white/[0.04] rounded-lg border border-white/10 overflow-hidden">
            <div className="absolute inset-y-0 left-0 bg-yellow-500/70 flex items-center justify-end pr-2" style={{ width: `${reactPct}%` }}>
              {reactPct > 12 && <span className="text-[10px] font-bold text-black font-mono-spec">RÉACTION {detail.dReact}m</span>}
            </div>
            <div className="absolute inset-y-0 bg-red-600 flex items-center justify-end pr-2" style={{ left: `${reactPct}%`, width: `${freinPct}%` }}>
              {freinPct > 15 && <span className="text-[10px] font-bold text-white font-mono-spec">FREINAGE {detail.dFrein}m</span>}
            </div>
            {/* Reference markers */}
            {[50, 100, 150, 200].map((m) => (
              <div key={m} className="absolute inset-y-0 border-l border-white/15 text-[9px] text-white/40 font-mono-spec pl-1 pt-0.5" style={{ left: `${(m / maxScale) * 100}%` }}>
                {m}m
              </div>
            ))}
          </div>
          <div className="flex gap-4 mt-3 text-[11.5px] font-mono-spec">
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-yellow-500/70" /> Réaction</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-red-600" /> Freinage</span>
          </div>
        </div>

        {/* Détails calcul */}
        <div className="mt-7 space-y-2 text-[13px]">
          <div className="flex justify-between py-2 border-b border-white/5">
            <span className="text-white/70">Distance de réaction</span>
            <span className="text-white font-mono-spec">{detail.dReact} m</span>
          </div>
          <div className="flex justify-between py-2 border-b border-white/5">
            <span className="text-white/70">Distance de freinage ({e.label})</span>
            <span className="text-white font-mono-spec">{detail.dFrein} m</span>
          </div>
          <div className="flex justify-between py-2 border-b border-white/5">
            <span className="text-white/70">Énergie cinétique (vs 50 km/h)</span>
            <span className={`font-mono-spec ${detail.ecRatio > 2 ? 'text-red-400' : 'text-white'}`}>×{detail.ecRatio}</span>
          </div>
        </div>

        <div className="mt-6 p-4 rounded-xl border border-yellow-900/40 bg-yellow-950/10 text-yellow-200/85 text-[12px] flex gap-2 items-start">
          <Info size={13} className="shrink-0 mt-0.5" />
          <span>
            <strong>Formules officielles :</strong> d<sub>réaction</sub> = v/3,6 × t · d<sub>freinage</sub> = v²/{e.coef} (coef. d'adhérence {e.label.toLowerCase()}). L'énergie cinétique varie au carré de la vitesse — doubler la vitesse quadruple la violence du choc.
          </span>
        </div>
      </div>
    </div>
  );
}

// =====================================================================
// PAGE PRINCIPALE
// =====================================================================

const TABS = [
  { id: 'cout', label: 'Coût du permis', icon: Euro },
  { id: 'points', label: 'Calculateur de points', icon: Award },
  { id: 'distance', label: 'Distance d\'arrêt', icon: Gauge },
];

export default function SimulateursPage() {
  const [tab, setTab] = useState('cout');

  return (
    <PageShell>
      <section className="max-w-7xl mx-auto px-4 lg:px-6 mt-8 sm:mt-12">
        <SectionTitle
          kicker="Outils interactifs · Code Voiture"
          title="3 simulateurs pour passer du calcul à la décision"
          sub="Estimez votre budget permis, suivez votre solde de points et visualisez vos distances d'arrêt. Données officielles Sécurité Routière 2026 — sans pub, sans collecte."
        />

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6 sm:mb-8 border-b border-white/10 pb-3 -mx-1 sm:mx-0 overflow-x-auto hide-scroll">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.id}
                data-testid={`simu-tab-${t.id}`}
                onClick={() => setTab(t.id)}
                className={`inline-flex items-center gap-2 px-4 sm:px-5 h-10 sm:h-11 rounded-full text-[12.5px] sm:text-[13.5px] font-semibold border transition whitespace-nowrap ${tab === t.id ? 'border-red-500 bg-red-600 text-white' : 'border-white/15 bg-white/5 text-white/70 hover:border-white/30 hover:text-white'}`}
              >
                <Icon size={15} /> {t.label}
              </button>
            );
          })}
        </div>

        <div data-testid={`simu-content-${tab}`}>
          {tab === 'cout' && <CoutPermisSimulator />}
          {tab === 'points' && <PointsSimulator />}
          {tab === 'distance' && <DistanceArretSimulator />}
        </div>

        {/* Mini intro pédagogique sous les outils */}
        <div className="mt-10 sm:mt-14 grid md:grid-cols-3 gap-3 sm:gap-4">
          {[
            { t: 'Budget réaliste', d: 'Comparez les formules (B, AAC, CS, BEA) et anticipez les coûts cachés : représentations, RDV pédagogiques.', i: Euro },
            { t: 'Permis sous contrôle', d: 'Comprenez le mécanisme du permis probatoire et l\'impact réel d\'une infraction sur votre solde.', i: Award },
            { t: 'Vitesse = violence', d: 'La distance d\'arrêt double sur sol mouillé. L\'énergie cinétique, elle, quadruple si vous doublez la vitesse.', i: Gauge },
          ].map((c, i) => {
            const Icon = c.i;
            return (
              <div key={i} className="rounded-xl sm:rounded-2xl border border-white/10 bg-[#0d0d0e] p-4 sm:p-6">
                <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-red-600/15 border border-red-700/30 flex items-center justify-center text-red-400 mb-3 sm:mb-4"><Icon size={16} /></div>
                <h4 className="font-serif-editorial text-white text-[17px] sm:text-[20px] mb-1.5 sm:mb-2">{c.t}</h4>
                <p className="text-white/60 text-[12.5px] sm:text-[13.5px] leading-relaxed">{c.d}</p>
              </div>
            );
          })}
        </div>

        <div className="mt-8 sm:mt-10 text-center">
          <p className="text-white/40 text-[11.5px] sm:text-[12.5px] font-mono-spec uppercase tracking-wider mb-3">Calculs basés sur les sources officielles</p>
          <p className="text-white/55 text-[12.5px] sm:text-[13px] max-w-2xl mx-auto">
            Sécurité Routière, Articles L223-1 à L223-8 du Code de la route, ANTAI, observatoires départementaux des prix auto-école 2026.
          </p>
        </div>
      </section>
    </PageShell>
  );
}
